import { LngText } from '@shared/components/LngText';
import { ProcessFieldRegistryTableField } from '@shared/components/ProcessFieldDynamic/ProcessFieldRegistryTableField.tsx';
import { ProcessFieldTableField } from '@shared/components/ProcessFieldDynamic/ProcessFieldTableField.tsx';
import useLangText from '@shared/hooks/useLangText';
import { ProcessField, ServiceVM } from '@shared/types/process';

import { ProcessFieldAiChatLayout } from './ProcessFieldAiChatLayout';
import { ProcessFieldCheckboxField } from './ProcessFieldCheckboxField';
import { ProcessFieldDateField } from './ProcessFieldDate/ProcessFieldDateField';
import { ProcessFieldDateRangeField } from './ProcessFieldDateRange/ProcessFieldDateRangeField';
import { ProcessFieldFileField } from './ProcessFieldFile/ProcessFieldFileField';
import { ProcessFieldFloatField } from './ProcessFieldFloat/ProcessFieldFloatField';
import { ProcessFieldFloatFieldAiChat } from './ProcessFieldFloat/ProcessFieldFloatFieldAiChat';
import { ProcessFieldIntField } from './ProcessFieldInt/ProcessFieldIntField';
import { ProcessFieldIntFieldAiChat } from './ProcessFieldInt/ProcessFieldIntFieldAiChat';
import { ProcessFieldMapField } from './ProcessFieldMapField/ProcessFieldMapField';
import { ProcessFieldPhotoField } from './ProcessFieldPhoto/ProcessFieldPhotoField';
import { ProcessFieldSelectField } from './ProcessFieldSelect/ProcessFieldSelectField';
import { ProcessFieldSelectFieldAiChat } from './ProcessFieldSelect/ProcessFieldSelectFieldAiChat';
import { ProcessFieldSelectServiceField } from './ProcessFieldSelectIdp/ProcessFieldSelectServiceField';
import { ProcessFieldStringField } from './ProcessFieldString/ProcessFieldStringField';
import { ProcessFieldStringFieldAiChat } from './ProcessFieldString/ProcessFieldStringFieldAiChat';
import { ProcessFieldTextField } from './ProcessFieldText/ProcessFieldTextField';
import { ProcessFieldTextFieldAiChat } from './ProcessFieldText/ProcessFieldTextFieldAiChat';

import { useMemo } from 'react';

type FixProps<T extends React.ElementType> = Omit<
    React.ComponentPropsWithoutRef<T>,
    'field'
>;

function ProcessFieldDynamicInner({
    field,
    variant = 'default',
    aiChatShowSearch,
    ..._props
}: {
    field: ProcessField;
    service?: ServiceVM;
    variant?: 'default' | 'ai-chat';
    aiChatShowSearch?: boolean;
} & (
    | Omit<React.ComponentPropsWithoutRef<typeof ProcessFieldIntField>, 'field'>
    | Omit<
          React.ComponentPropsWithoutRef<typeof ProcessFieldFloatField>,
          'field'
      >
    | Omit<
          React.ComponentPropsWithoutRef<typeof ProcessFieldTextField>,
          'field'
      >
)) {
    const getLangText = useLangText();
    const placeholder = _props.placeholder ?? getLangText(field?.name);

    const defaultValue = useMemo(() => {
        if (!field) {
            return undefined;
        }

        try {
            return field.defaultValue
                ? JSON.parse(field.defaultValue)
                : undefined;
        } catch (e) {
            console.debug('Error parsing default value:', e);
            return undefined;
        }
    }, [field]);

    const commonProps = useMemo(() => {
        if (!field) {
            return {};
        }

        return {
            name: field.id,
            initialValue: defaultValue,
            label: <LngText record={field.name} />,
        };
    }, [defaultValue, field]);

    if (!field) {
        return null;
    }

    if (field.typeId === 'Int') {
        const props = _props as FixProps<typeof ProcessFieldIntField>;
        const IntField =
            variant === 'ai-chat'
                ? ProcessFieldIntFieldAiChat
                : ProcessFieldIntField;

        return (
            <IntField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
            />
        );
    }

    if (field.typeId === 'Float') {
        const props = _props as FixProps<typeof ProcessFieldFloatField>;
        const FloatField =
            variant === 'ai-chat'
                ? ProcessFieldFloatFieldAiChat
                : ProcessFieldFloatField;

        return (
            <FloatField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
            />
        );
    }

    if (field.typeId === 'Text') {
        const props = _props as FixProps<typeof ProcessFieldTextField>;
        const TextField =
            variant === 'ai-chat'
                ? ProcessFieldTextFieldAiChat
                : ProcessFieldTextField;

        return (
            <TextField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
            />
        );
    }

    if (field.typeId === 'String') {
        const props = _props as FixProps<typeof ProcessFieldStringField>;
        const StringField =
            variant === 'ai-chat'
                ? ProcessFieldStringFieldAiChat
                : ProcessFieldStringField;

        return (
            <StringField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
            />
        );
    }

    if (field.typeId === 'Table') {
        const props = _props as FixProps<typeof ProcessFieldTableField>;

        return (
            <ProcessFieldTableField
                field={field}
                {...commonProps}
                {...props}
                service={(_props as { service?: ServiceVM }).service}
            />
        );
    }

    if (field.typeId === 'RegistryTable') {
        const props = _props as FixProps<typeof ProcessFieldRegistryTableField>;

        return (
            <ProcessFieldRegistryTableField
                field={field}
                {...commonProps}
                {...props}
                service={(_props as { service?: ServiceVM }).service}
            />
        );
    }

    if (field.typeId === 'Date') {
        const props = _props as FixProps<typeof ProcessFieldDateField>;

        return (
            <ProcessFieldDateField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
                variant={variant}
            />
        );
    }

    if (field.typeId === 'DateRange') {
        const props = _props as FixProps<typeof ProcessFieldDateRangeField>;

        return (
            <ProcessFieldDateRangeField
                field={field}
                {...commonProps}
                placeholders={[placeholder, placeholder]}
                {...props}
                variant={variant}
            />
        );
    }

    if (field.typeId === 'Select') {
        const props = _props as FixProps<typeof ProcessFieldSelectField>;

        if (variant === 'ai-chat') {
            return (
                <ProcessFieldSelectFieldAiChat
                    field={field}
                    {...commonProps}
                    {...props}
                    placeholder={placeholder}
                    showSearch={aiChatShowSearch}
                />
            );
        }

        return (
            <ProcessFieldSelectField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
            />
        );
    }

    if (field.typeId === 'SelectIdp') {
        const props = _props as FixProps<typeof ProcessFieldSelectServiceField>;

        return (
            <ProcessFieldSelectServiceField
                field={field}
                {...commonProps}
                {...props}
                placeholder={placeholder}
                variant={variant}
                aiChatShowSearch={aiChatShowSearch}
            />
        );
    }

    if (field.typeId === 'File') {
        const props = _props as FixProps<typeof ProcessFieldFileField>;

        return (
            <ProcessFieldFileField
                field={field}
                {...commonProps}
                {...props}
                variant={variant}
            />
        );
    }

    if (field.typeId === 'Photo') {
        const props = _props as FixProps<typeof ProcessFieldPhotoField>;

        return (
            <ProcessFieldPhotoField field={field} {...commonProps} {...props} />
        );
    }

    if (field.typeId === 'Checkbox') {
        const props = _props as FixProps<typeof ProcessFieldCheckboxField>;

        return (
            <ProcessFieldCheckboxField
                field={field}
                {...commonProps}
                {...props}
            />
        );
    }

    if (field.typeId === 'Map') {
        const props = _props as FixProps<typeof ProcessFieldMapField>;

        return (
            <ProcessFieldMapField field={field} {...commonProps} {...props} />
        );
    }

    return 'Not implemented yet';
}

export function ProcessFieldDynamic(
    props: React.ComponentPropsWithoutRef<typeof ProcessFieldDynamicInner>,
) {
    const content = <ProcessFieldDynamicInner {...props} />;

    if (props.variant === 'ai-chat' && props.field) {
        return (
            <ProcessFieldAiChatLayout fieldType={props.field.typeId}>
                {content}
            </ProcessFieldAiChatLayout>
        );
    }

    return content;
}
